import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { useCompleteLine } from '../../../List/hooks';
import { useToast } from '@shared/hooks/use-toast';
import { ExtraWorkOrderLine } from '../../../List/models';
import { RegisterWasteModal } from '../../../List/components/modals';
import { translateApiError } from '../../../List/utils/errorTranslator';

interface QuantityControlsProps {
  orderId: number;
  line: ExtraWorkOrderLine;
  activeCartCode?: string; // Código del carrito activo para usar como ubicación destino
  onSuccess?: () => void;
  onReset?: () => void; // Callback para resetear al paso inicial
}

/**
 * Controles de cantidad - Input y botones para añadir o mermar
 *
 * Aparece después de escanear el grupo, debajo del carrito activo
 */
export const QuantityControls = ({ orderId, line, activeCartCode, onSuccess, onReset }: QuantityControlsProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');
  const [quantity, setQuantity] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const registerWasteModalRef = useRef<ModalCenterRef>(null);
  const { toast } = useToast();

  const completeLineMutation = useCompleteLine();

  // Auto-focus al montar el componente
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Validar que la cantidad sea un número válido
  const validateQuantity = (): number | null => {
    const qty = Number(quantity);
    if (!quantity.trim() || isNaN(qty) || qty <= 0) {
      toast({
        title: t('detail.quantity.error.title', 'Error'),
        description: t('detail.quantity.error.invalid', 'La cantidad debe ser un número mayor a 0'),
        variant: 'destructive',
      });
      return null;
    }
    return qty;
  };

  // Handler para añadir cantidad - Llama al endpoint de finalizar (complete)
  const handleAdd = async () => {
    console.log('🔍 QuantityControls - handleAdd llamado');
    const qty = validateQuantity();
    console.log('🔍 QuantityControls - Cantidad validada:', qty);

    if (!qty || isProcessing) {
      console.log('🔍 QuantityControls - Validación falló o está procesando');
      return;
    }

    // Validar que tenemos carrito activo (ubicación destino)
    if (!activeCartCode) {
      console.error('❌ QuantityControls - No hay carrito activo:', activeCartCode);
      toast({
        title: t('detail.quantity.error.title', 'Error'),
        description: t('detail.quantity.error.no_cart', 'No hay carrito activo. Debe escanear un carrito primero'),
        variant: 'destructive',
      });
      return;
    }

    setIsProcessing(true);
    try {
      console.log('🔍 QuantityControls - Finalizando línea (añadir cantidad):', {
        orderId,
        lineId: line.id,
        qty,
        activeCartCode,
        location_code: activeCartCode, // Usar el código del carrito como ubicación destino
      });

      // Validar que tenemos el código del carrito
      if (!activeCartCode || !activeCartCode.trim()) {
        console.error('❌ QuantityControls - Código de carrito inválido:', activeCartCode);
        toast({
          title: t('detail.quantity.error.title', 'Error'),
          description: t('detail.quantity.error.invalid_cart_code', 'El código del carrito no es válido'),
          variant: 'destructive',
        });
        return;
      }

      // Llamar al endpoint de finalizar (complete) usando el carrito activo como ubicación destino
      const response = await completeLineMutation.mutateAsync({
        orderId,
        lineId: line.id,
        data: {
          quantity_completed: qty, // Cantidad completada ingresada por el usuario
          location_code: activeCartCode.trim(), // El código del carrito es la ubicación destino
        },
      });

      console.log('✅ QuantityControls - Línea finalizada exitosamente:', response);

      // Limpiar input después de éxito
      setQuantity('');

      // Ejecutar callback: el padre refetch y vuelve al paso del carrito
      onSuccess?.();
    } catch (error: any) {
      console.error('❌ QuantityControls - Error al finalizar línea:', error);
      console.error('❌ QuantityControls - Error details:', {
        message: error?.message,
        response: error?.response,
        data: error?.response?.data,
      });

      // Usar translateApiError para internacionalizar el mensaje
      const errorMessage = translateApiError(error, t);

      toast({
        title: t('detail.quantity.error.title', 'Error'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Handler para mermar cantidad - Abre el modal
  const handleWaste = () => {
    const qty = validateQuantity();
    if (!qty || isProcessing) return;

    // Abrir el modal de mermar con la cantidad predefinida
    registerWasteModalRef.current?.openModal();
  };

  // Handler cuando se registra la merma exitosamente
  const handleWasteRegistered = () => {
    // Limpiar input después de éxito
    setQuantity('');

    // Ejecutar callback: el padre refetch y vuelve al paso del carrito
    onSuccess?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // Por defecto, al presionar Enter se añade
      handleAdd();
    }
  };

  return (
    <>
      <div className="bg-card-bg border border-card-border rounded-lg p-6 space-y-4">
        {/* Input de cantidad */}
        <div>
          <label className="block text-sm font-medium text-text mb-2">{t('detail.quantity.label', 'Cantidad')}</label>
          <Input
            ref={inputRef}
            type="number"
            min="1"
            placeholder={t('detail.quantity.placeholder', 'Ingresa la cantidad...')}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isProcessing}
            className="w-full text-center"
            data-testid="quantity-input"
          />
        </div>

        {/* Botones de acción */}
        <div className="flex gap-3">
          <Button
            variant="primary"
            size="lg"
            onClick={handleAdd}
            disabled={!quantity.trim() || isProcessing}
            className="flex-1"
            data-testid="add-quantity-button"
          >
            {isProcessing && completeLineMutation.isPending
              ? t('detail.quantity.adding', 'Añadiendo...')
              : t('detail.quantity.add', 'Añadir')}
          </Button>
          <Button
            variant="outline"
            size="lg"
            onClick={handleWaste}
            disabled={!quantity.trim() || isProcessing}
            className="flex-1"
            data-testid="waste-quantity-button"
          >
            {t('detail.quantity.waste', 'Mermar')}
          </Button>
        </div>

        {/* Modal de mermar */}
        <RegisterWasteModal
          ref={registerWasteModalRef}
          orderId={orderId}
          lineId={line.id}
          qty={Number(quantity) || undefined}
          onSuccess={handleWasteRegistered}
        />
      </div>
      {/* Reiniciar proceso - Enlace tipo texto, regresa al primer paso (escáner de carrito) */}
      {onReset && (
        <div className="pt-3 text-center">
          <button
            type="button"
            onClick={onReset}
            disabled={isProcessing}
            className="inline-flex items-center gap-2 text-complementary-text bg-transparent border-none cursor-pointer hover:underline hover:text-text disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline p-0 text-sm font-medium"
            data-testid="restart-process-button"
          >
            <FontAwesomeIcon icon={faArrowsRotate} className="w-3.5 h-3.5" />
            {t('detail.quantity.restart_process', 'Reiniciar proceso')}
          </button>
        </div>
      )}
    </>
  );
};
